import _ from "lodash";
import { arrayMove } from "@dnd-kit/sortable";
import {
  createEmptySection,
  createFieldFromPalette,
} from "./formBuilderFieldFactory";

export const parseDragId = (id) => {
  if (!id || typeof id !== "string") return null;

  if (id.startsWith("palette:")) {
    return { kind: "palette", fieldType: id.slice(8) };
  }

  if (id.startsWith("section:")) {
    return { kind: "section", sectionIndex: Number(id.slice(8)) };
  }

  if (id.startsWith("field:")) {
    const [sectionIndex, fieldIndex] = id.slice(6).split(":").map(Number);
    return { kind: "field", sectionIndex, fieldIndex };
  }

  if (id.startsWith("subfield:")) {
    const [sectionIndex, fieldIndex, subFieldIndex] = id
      .slice(9)
      .split(":")
      .map(Number);
    return { kind: "subfield", sectionIndex, fieldIndex, subFieldIndex };
  }

  if (id.startsWith("zone:section:")) {
    return { kind: "zone-section", sectionIndex: Number(id.slice(13)) };
  }

  if (id.startsWith("zone:subsection:")) {
    const [sectionIndex, fieldIndex] = id.slice(16).split(":").map(Number);
    return { kind: "zone-subsection", sectionIndex, fieldIndex };
  }

  if (id === "zone:canvas") {
    return { kind: "zone-canvas" };
  }

  return null;
};

export const resolveDropTarget = (over) => {
  if (!over) return null;

  const parsed = parseDragId(String(over.id));
  if (parsed) return parsed;

  const overData = over.data?.current;
  if (overData?.dropTarget) {
    return overData.dropTarget;
  }

  if (overData?.type === "section") {
    return { kind: "section", sectionIndex: overData.sectionIndex };
  }

  if (overData?.type === "field") {
    return {
      kind: "field",
      sectionIndex: overData.sectionIndex,
      fieldIndex: overData.fieldIndex,
    };
  }

  if (overData?.type === "subfield") {
    return {
      kind: "subfield",
      sectionIndex: overData.sectionIndex,
      fieldIndex: overData.fieldIndex,
      subFieldIndex: overData.subFieldIndex,
    };
  }

  return null;
};

const cloneTemplate = (template) => _.cloneDeep(template);

const isSubSectionContainer = (field) => field?.type === "sub_section";

const isNestedTarget = (target) =>
  target?.kind === "zone-subsection" || target?.kind === "subfield";

export const normalizePaletteTarget = (target, template) => {
  if (!target) {
    if (!template.length) return null;
    return { kind: "zone-section", sectionIndex: template.length - 1 };
  }

  if (target.kind === "section") {
    return { kind: "zone-section", sectionIndex: target.sectionIndex };
  }

  if (target.kind === "zone-canvas") {
    return { kind: "zone-section", sectionIndex: Math.max(template.length - 1, 0) };
  }

  return target;
};

export const normalizeFieldMoveTarget = (target) => {
  if (!target) return null;

  if (target.kind === "section") {
    return { kind: "zone-section", sectionIndex: target.sectionIndex };
  }

  return target;
};

const adjustTargetAfterExtract = (activeMeta, target) => {
  if (!target) return target;

  const nextTarget = { ...target };

  if (activeMeta.kind === "field") {
    if (
      (nextTarget.kind === "field" ||
        nextTarget.kind === "zone-subsection" ||
        nextTarget.kind === "subfield") &&
      activeMeta.sectionIndex === nextTarget.sectionIndex &&
      activeMeta.fieldIndex < nextTarget.fieldIndex
    ) {
      nextTarget.fieldIndex -= 1;
    }
  }

  if (activeMeta.kind === "subfield") {
    if (
      nextTarget.kind === "subfield" &&
      activeMeta.sectionIndex === nextTarget.sectionIndex &&
      activeMeta.fieldIndex === nextTarget.fieldIndex &&
      activeMeta.subFieldIndex < nextTarget.subFieldIndex
    ) {
      nextTarget.subFieldIndex -= 1;
    }
  }

  return nextTarget;
};

const extractField = (template, source) => {
  const next = cloneTemplate(template);

  if (source.kind === "field") {
    const [field] = next[source.sectionIndex].input_fields.splice(
      source.fieldIndex,
      1
    );
    return { template: next, field };
  }

  if (source.kind === "subfield") {
    const subSection =
      next[source.sectionIndex].input_fields[source.fieldIndex];
    const [field] = subSection.sub_fields.splice(source.subFieldIndex, 1);
    return { template: next, field };
  }

  return { template: next, field: null };
};

const insertField = (template, target, field) => {
  const next = cloneTemplate(template);

  if (target.kind === "zone-section" || target.kind === "field") {
    const sectionIndex = target.sectionIndex;
    const insertAt =
      target.kind === "field"
        ? target.fieldIndex
        : next[sectionIndex].input_fields.length;

    next[sectionIndex].input_fields.splice(insertAt, 0, field);
    return {
      template: next,
      sectionIndex,
      fieldIndex: insertAt,
      subFieldIndex: null,
    };
  }

  if (target.kind === "zone-subsection" || target.kind === "subfield") {
    const sectionIndex = target.sectionIndex;
    const fieldIndex = target.fieldIndex;
    const subSection = next[sectionIndex].input_fields[fieldIndex];

    if (!subSection?.sub_fields) {
      subSection.sub_fields = [];
    }

    const insertAt =
      target.kind === "subfield"
        ? target.subFieldIndex
        : subSection.sub_fields.length;

    subSection.sub_fields.splice(insertAt, 0, field);
    return {
      template: next,
      sectionIndex,
      fieldIndex,
      subFieldIndex: insertAt,
    };
  }

  return {
    template: next,
    sectionIndex: null,
    fieldIndex: null,
    subFieldIndex: null,
  };
};

const canInsertFieldAtTarget = (field, target) => {
  if (!field || !target) return false;

  if (isSubSectionContainer(field) && isNestedTarget(target)) {
    return false;
  }

  if (isSubSectionContainer(field) && target.kind === "subfield") {
    return false;
  }

  return true;
};

export const addPaletteField = (template, fieldType, target) => {
  let nextTemplate = cloneTemplate(template);
  const createdSection = !nextTemplate.length;

  if (createdSection) {
    nextTemplate = [createEmptySection("Section 1")];
  }

  const normalizedTarget = normalizePaletteTarget(target, nextTemplate);
  if (!normalizedTarget) return null;

  const field = createFieldFromPalette(fieldType);
  const result = insertField(nextTemplate, normalizedTarget, field);
  return { ...result, field, createdSection };
};

export const moveExistingField = (template, active, over) => {
  if (!over || active.id === over.id) return null;

  const activeMeta = parseDragId(String(active.id));
  const overMeta = normalizeFieldMoveTarget(resolveDropTarget(over));

  if (!activeMeta || !["field", "subfield"].includes(activeMeta.kind)) {
    return null;
  }

  if (!overMeta) return null;

  if (activeMeta.kind === "field" && overMeta.kind === "field") {
    if (activeMeta.sectionIndex === overMeta.sectionIndex) {
      const next = cloneTemplate(template);
      next[activeMeta.sectionIndex].input_fields = arrayMove(
        next[activeMeta.sectionIndex].input_fields,
        activeMeta.fieldIndex,
        overMeta.fieldIndex
      );
      return { template: next };
    }
  }

  if (activeMeta.kind === "subfield" && overMeta.kind === "subfield") {
    if (
      activeMeta.sectionIndex === overMeta.sectionIndex &&
      activeMeta.fieldIndex === overMeta.fieldIndex
    ) {
      const next = cloneTemplate(template);
      const subSection =
        next[activeMeta.sectionIndex].input_fields[activeMeta.fieldIndex];
      subSection.sub_fields = arrayMove(
        subSection.sub_fields,
        activeMeta.subFieldIndex,
        overMeta.subFieldIndex
      );
      return { template: next };
    }
  }

  const targetKinds = ["zone-section", "zone-subsection", "field", "subfield"];
  if (!targetKinds.includes(overMeta.kind)) {
    return null;
  }

  const { template: withoutField, field } = extractField(template, activeMeta);
  if (!field) return null;

  if (isSubSectionContainer(field) && isNestedTarget(overMeta)) {
    return null;
  }

  const adjustedTarget = adjustTargetAfterExtract(activeMeta, overMeta);
  if (!canInsertFieldAtTarget(field, adjustedTarget)) {
    return null;
  }

  return insertField(withoutField, adjustedTarget, field);
};

export const reorderSections = (template, active, over) => {
  const activeMeta = parseDragId(String(active.id));
  let overMeta = resolveDropTarget(over);

  if (activeMeta?.kind !== "section") {
    return null;
  }

  if (overMeta?.kind === "zone-section") {
    overMeta = { kind: "section", sectionIndex: overMeta.sectionIndex };
  }

  if (overMeta?.kind === "field" || overMeta?.kind === "subfield") {
    overMeta = { kind: "section", sectionIndex: overMeta.sectionIndex };
  }

  if (overMeta?.kind !== "section") {
    return null;
  }

  if (activeMeta.sectionIndex === overMeta.sectionIndex) {
    return null;
  }

  return {
    template: arrayMove(
      cloneTemplate(template),
      activeMeta.sectionIndex,
      overMeta.sectionIndex
    ),
  };
};

export const getActiveDragKind = (activeId) => parseDragId(String(activeId))?.kind;

export const filterCollisionsForActive = (activeId, collisions) => {
  const activeKind = getActiveDragKind(activeId);
  if (!collisions?.length) return collisions;

  const allowedKindsByActive = {
    palette: ["zone-canvas", "zone-section", "zone-subsection", "field", "subfield", "section"],
    section: ["section", "zone-section"],
    field: ["zone-section", "zone-subsection", "field", "subfield"],
    subfield: ["zone-section", "zone-subsection", "field", "subfield"],
  };

  const allowed = allowedKindsByActive[activeKind];
  if (!allowed) return collisions;

  const filtered = collisions.filter((collision) => {
    const kind = parseDragId(String(collision.id))?.kind;
    return kind && allowed.includes(kind);
  });

  return filtered.length ? filtered : collisions;
};
